// The composer for an area template and the ruler's readout, over the board
// (issue 832). The canvas draws the shaded squares and the ruler's line; this
// is where a shape, size, direction and label are chosen, and where the
// distance is said in words — both by the table's system's distance rule
// (issue 1303): sizes in its unit, a step of one square; with no rule, an
// area cannot be laid and the ruler counts squares across and down.
import { useEffect, useRef, useState } from 'react';
import { AREA_LIMITS, rulerLabel, type AreaShape, type DistanceRules } from '@dragons-table/vtt-shared';
import { useTableState } from '../table/TableState';
import { areaTool, type AreaDraft } from './areaTool';
import { measureTool, type Ruler } from './measureTool';
import { difficultTest, dragReadout } from './dragReadout';
import { tapMoveStore } from './touch';
import { pressWord } from '../shell/device';
import { BTN } from '../shell/ui';

const btn = BTN;
const SHAPES: Array<[AreaShape, string]> = [['burst', 'Burst'], ['cone', 'Cone'], ['line', 'Line'], ['rect', 'Rectangle']];
const ARROWS = ['→', '↘', '↓', '↙', '←', '↖', '↑', '↗'];
const NAMES = ['east', 'south-east', 'south', 'south-west', 'west', 'north-west', 'north', 'north-east'];

function useArea(): AreaDraft {
  const [d, setD] = useState(areaTool.get());
  useEffect(() => areaTool.subscribe(setD), []);
  return d;
}
function useRuler(): Ruler {
  const [r, setR] = useState(measureTool.get());
  useEffect(() => measureTool.subscribe(setR), []);
  return r;
}

/** A size typed in the system's unit, as whole squares (at least one). */
const squaresOf = (v: string, dist: DistanceRules) => Math.max(1, Math.min(AREA_LIMITS.squares, Math.round((Number(v) || 0) / dist.perSquare)));
/** Squares in the system's unit, to a hundredth. */
const inUnit = (squares: number, dist: DistanceRules) => Math.round(squares * dist.perSquare * 100) / 100;

/**
 * A size box (issue 1421): what is typed stays as typed while the box has the focus, and the area follows it in whole
 * squares; leaving the box, or Enter, writes the squares back. Rounding each keystroke into the box turned a typed 10
 * into 50 (the 1 became one square, 5 ft, and the 0 went after it).
 */
function SizeBox({ label, squares, dist, onSquares }: { label: string; squares: number; dist: DistanceRules; onSquares: (n: number) => void }) {
  const [text, setText] = useState(String(inUnit(squares, dist)));
  const focused = useRef(false);
  useEffect(() => { if (!focused.current) setText(String(inUnit(squares, dist))); }, [squares, dist]);
  const settle = () => { setText(String(inUnit(squares, dist))); };
  return (
    <input aria-label={label} type="number" step={dist.perSquare} min={dist.perSquare} max={inUnit(AREA_LIMITS.squares, dist)} className="w-16 rounded bg-stone-800 px-1 py-0.5" value={text}
      onFocus={() => { focused.current = true; }}
      onBlur={() => { focused.current = false; settle(); }}
      onKeyDown={(e) => { if (e.key === 'Enter') settle(); }}
      onChange={(e) => { setText(e.target.value); onSquares(squaresOf(e.target.value, dist)); }} />
  );
}

export function AreaComposer() {
  const { act, meId, systemRules } = useTableState();
  const d = useArea();
  // A spell cast from the action bar (issue 1130) has its shape and size already: the bar says what to tap.
  if (!d.on || d.cast) return null;
  const dist = systemRules.distance;
  if (!dist) {
    return (
      <p className="pointer-events-auto flex items-center gap-2 self-start rounded bg-stone-900/95 px-3 py-2 text-xs text-stone-200" data-testid="area-composer">
        This table's game system has no distance rules, so an area cannot be measured out.
        <button type="button" className={btn} onClick={() => areaTool.stop()}>Cancel</button>
      </p>
    );
  }
  const directional = d.shape !== 'burst';
  return (
    <form className="pointer-events-auto flex max-w-2xl flex-wrap items-center gap-2 self-start rounded bg-stone-900/95 px-3 py-2 text-xs text-stone-200" data-testid="area-composer" aria-label="Lay an area template"
      onSubmit={(e) => {
        e.preventDefault();
        if (!d.origin) return;
        act({ kind: 'area.set', area: { id: crypto.randomUUID(), ownerId: meId, shape: d.shape, origin: d.origin, size: d.size, size2: d.shape === 'rect' ? d.size2 : d.size, diagonal: dist.diagonal, includes: systemRules.areaIncludes, dir: d.dir, label: d.label.trim().slice(0, AREA_LIMITS.label) } }, (reply) => { if (reply.ok) areaTool.stop(); });
      }}>
      <span className="font-medium text-amber-300">Lay an area</span>
      <label className="flex items-center gap-1">Shape
        <select aria-label="Shape" className="rounded bg-stone-800 px-1 py-0.5" value={d.shape} onChange={(e) => areaTool.change({ shape: e.target.value as AreaShape })}>
          {SHAPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </label>
      <label className="flex items-center gap-1">{d.shape === 'burst' ? 'Radius' : d.shape === 'rect' ? 'Width' : 'Length'}
        <SizeBox label={`${d.shape === 'burst' ? 'Radius' : d.shape === 'rect' ? 'Width' : 'Length'} in ${dist.unit}`} squares={d.size} dist={dist} onSquares={(size) => areaTool.change({ size })} /> {dist.unit}
      </label>
      {d.shape === 'rect' && (
        <label className="flex items-center gap-1">Height
          <SizeBox label={`Height in ${dist.unit}`} squares={d.size2} dist={dist} onSquares={(size2) => areaTool.change({ size2 })} /> {dist.unit}
        </label>
      )}
      {directional && (
        <span role="radiogroup" aria-label="Direction" className="flex gap-0.5">
          {ARROWS.map((a, k) => (
            <button key={a} type="button" role="radio" aria-checked={d.dir === k} aria-label={NAMES[k]} className={`${btn} ${d.dir === k ? 'border-amber-500 text-amber-300' : ''}`} onClick={() => areaTool.change({ dir: k })}>{a}</button>
          ))}
        </span>
      )}
      <input aria-label="Label" className="w-32 rounded bg-stone-800 px-2 py-0.5" placeholder="e.g. Fireball" maxLength={AREA_LIMITS.label} value={d.label} onChange={(e) => areaTool.change({ label: e.target.value })} />
      <span className="text-stone-400" data-testid="area-hint">{d.origin ? `${pressWord()} again to move it.` : `${pressWord()} the corner between squares where it starts.`} Everyone at the table sees it.</span>
      <button type="submit" className={`${btn} border-amber-500 text-amber-300`} disabled={!d.origin}>Lay area</button>
      <button type="button" className={btn} onClick={() => areaTool.stop()}>Cancel</button>
    </form>
  );
}

/** The distance, in words, while measuring; a line through difficult terrain this screen holds is costed (issue 1151). */
export function RulerReadout() {
  const { systemRules, store } = useTableState();
  const r = useRuler();
  if (!r.on) return null;
  const text = r.from && r.to ? rulerLabel(r.from, r.to, systemRules.distance, difficultTest(store.state)) : 'Drag from one square to another.';
  return (
    <p className="pointer-events-auto flex items-center gap-2 self-start rounded bg-stone-900/95 px-3 py-1 text-xs text-stone-200" data-testid="ruler" role="status">
      <span className="font-medium text-amber-300">Measure</span>
      <span data-testid="ruler-distance">{text}</span>
      <button type="button" className={btn} onClick={() => measureTool.stop()}>Done</button>
    </p>
  );
}

/** What the token being dragged would spend (issue 1151), by the table's rule; nothing between drags. */
export function DragReadout() {
  const [text, setText] = useState(dragReadout.get());
  useEffect(() => dragReadout.subscribe(setText), []);
  // Tap to move (issue 1178): between drags, a token waiting for its square says what to do next.
  const [waiting, setWaiting] = useState(tapMoveStore.get());
  useEffect(() => tapMoveStore.subscribe(setWaiting), []);
  if (!text && waiting) {
    return (
      <p className="pointer-events-none rounded bg-stone-900/95 px-3 py-1 text-xs text-sky-200" data-testid="tap-move-hint" role="status">
        Tap a square to move. Tap the token again to cancel.
      </p>
    );
  }
  if (!text) return null;
  return (
    <p className="pointer-events-none rounded bg-stone-900/95 px-3 py-1 text-xs text-stone-200" data-testid="drag-cost" role="status">
      <span className="font-medium text-amber-300">Move</span> {text}
    </p>
  );
}
